/*
 * Copyright (c) 2026. Tooling Team
 */

import { useMemo } from "react";
import PropTypes from "prop-types";
import { Button } from "@uhg-abyss/web/ui/Button";
import { DataTable } from "@uhg-abyss/web/ui/DataTable";
import { useDataTable } from "@uhg-abyss/web/hooks/useDataTable";
import { useGetLabDomainCodeEvidenceQuery } from "../../../../app/services/labDomainApi";
import {
  getEvidenceColumns,
  getRowsWithInternalIds,
  LAB_DOMAIN_EVIDENCE_ROW_ID,
} from "../utils/codeEvidenceTable";

// Abyss's expandColumnConfig.subComponentHeight is a fixed number (no auto height),
// so the panel scrolls internally instead of growing with its content.
export const DISCLOSURE_PANEL_HEIGHT = 400;

// The panel renders inside the outer table's <td colSpan>, a flex item with no overflow of
// its own. Without overflow:hidden + minWidth:0 a wide evidence table stretches the whole
// expanded row instead of scrolling internally.
const PANEL_STYLE = {
  boxSizing: "border-box",
  height: DISCLOSURE_PANEL_HEIGHT,
  minWidth: 0,
  overflow: "hidden",
  padding: "16px",
  width: "100%",
};

const PANEL_HEADER_STYLE = {
  alignItems: "center",
  display: "flex",
  justifyContent: "space-between",
  marginBottom: "12px",
};

const PANEL_BODY_STYLE = {
  height: `calc(100% - 48px)`,
  minWidth: 0,
  overflow: "auto",
};

// Mounted only once rows exist so initialData/initialColumns are correct from the first render.
// useDataTable's initialData is a one-time seed, not reactive.
function EvidenceTable({ rows }) {
  const tableRows = useMemo(() => getRowsWithInternalIds(rows), [rows]);
  const columns = useMemo(() => getEvidenceColumns(rows), [rows]);

  const tableState = useDataTable({
    initialData: tableRows,
    initialColumns: columns,
    rowIdKey: LAB_DOMAIN_EVIDENCE_ROW_ID,
    tableConfig: {
      enableColumnResizing: true,
      columnResizeMode: "onEnd",
    },
    paginationConfig: { enablePagination: true, pageSizeOptions: [10, 25, 50] },
  });

  return (
    <DataTable hideHeader title="Code Evidence" tableState={tableState}>
      <DataTable.Table />
      <DataTable.SlotWrapper>
        <DataTable.Pagination showPageSizeDropdown />
      </DataTable.SlotWrapper>
    </DataTable>
  );
}

EvidenceTable.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export const hasCodeEvidenceParams = (row) =>
  Boolean(row?.mapUuid && row?.localCodeSystem && row?.localCode);

export default function CodeEvidenceDisclosurePanel({ row, onClose }) {
  const {
    data: codeEvidenceData,
    error,
    isError,
    isFetching,
    isLoading,
    refetch,
  } = useGetLabDomainCodeEvidenceQuery(
    {
      mapUuid: row?.mapUuid,
      localSystem: row?.localCodeSystem,
      localCode: row?.localCode,
    },
    { skip: !hasCodeEvidenceParams(row) }
  );

  const rows = Array.isArray(codeEvidenceData) ? codeEvidenceData : [];
  const errorMessage =
    error?.data?.message ||
    error?.error ||
    "Unable to load Lab Domain code evidence.";

  let body;
  if ((isLoading || isFetching) && !rows.length) {
    body = <p>Loading Lab Domain code evidence data...</p>;
  } else if (isError) {
    body = (
      <>
        <p>{errorMessage}</p>
        <Button onClick={refetch}>Retry</Button>
      </>
    );
  } else if (!rows.length) {
    body = <p>No Lab Domain code evidence records were found.</p>;
  } else {
    body = <EvidenceTable rows={rows} />;
  }

  return (
    <div style={PANEL_STYLE}>
      <div style={PANEL_HEADER_STYLE}>
        <strong>Code Evidence: {row?.localCode}</strong>
        <Button
          aria-label={`Close Code Evidence for ${row?.localCode}`}
          color="neutral"
          onClick={onClose}
          size="sm"
          variant="outline"
        >
          Close
        </Button>
      </div>
      <div style={PANEL_BODY_STYLE}>{body}</div>
    </div>
  );
}

CodeEvidenceDisclosurePanel.propTypes = {
  row: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
};
